import { comparePayloads } from '@fluid-experimental/tree';
import { assertPrecondition, Result } from '../../errors';
import { hasAnyElement, removeDuplicates } from '../../utility';
import { hasTraits, MfsPropertyTrait } from '../mfsProperty';
import { add, MfsDeltaValue, MfsValue, remove } from '../mfsValue';
import { isMfsDeltaValue, isMfsValueArray } from '../mfsValueTypeGuards';

export type CheckedDeltaValue = {
  add: MfsValue[];
  remove: MfsValue[];
};

/**
 * Eliminates:
 *
 * - Duplicates in removeValues.
 * - Values in both add & remove.
 */
export function sanitizeDelta(delta: MfsDeltaValue): MfsDeltaValue {
  const valuesToRemove = delta[remove];
  const valuesToAdd = delta[add];

  if (valuesToRemove && hasAnyElement(valuesToRemove)) {
    const uniqueValuesToRemove = removeDuplicates(valuesToRemove, comparePayloads);

    if (valuesToAdd && hasAnyElement(valuesToAdd)) {
      const delta: CheckedDeltaValue = {
        add: [],
        remove: uniqueValuesToRemove,
      };

      // eliminate common Values.
      const result: CheckedDeltaValue = valuesToAdd.reduce<CheckedDeltaValue>((acc: CheckedDeltaValue, a) => {
        const i = uniqueValuesToRemove.findIndex((r) => comparePayloads(a, r));

        if (i > -1) {
          // skip that element from both add and remove
          acc.remove.splice(i, 1);
          return acc;
        }

        (acc.add ||= []).push(a);
        return acc;
      }, delta);

      return MfsDeltaValue.from({ addValues: result.add, removeValues: result.remove });
    }

    return MfsDeltaValue.from({ addValues: valuesToAdd, removeValues: uniqueValuesToRemove });
  }

  return delta;
}

/**
 * Gets the multi-values by deduplicating the passed values if there's uniqueness constraint defined (only supported for
 * base properties currently.)
 */
function getEffectiveMultiValue(propertyKey: string, propertyValue: MfsValue[]): Result<MfsValue[]> {
  if (propertyValue && hasAnyElement(propertyValue) && hasUniquenessConstraint(propertyKey)) {
    const dedupedValues = removeDuplicates(propertyValue);
    return Result.ok(dedupedValues);
  }

  return Result.ok(propertyValue);
}

/**
 * Gets the delta values after:
 *
 * - Eliminated values that are both added and removed
 * - Eliminated duplicate removed values.
 * - Deduplicated addValues if there's uniqueness constraint defined (only supported for base properties.)
 */
function getEffectiveDeltaValue(propertyKey: string, propertyValue: MfsDeltaValue): Result<MfsDeltaValue> {
  const sanitizedDelta = sanitizeDelta(propertyValue);
  const { hasAdd, hasRemove } = checkDelta(sanitizedDelta);

  if (!hasAdd && !hasRemove) {
    return Result.fail(new Error('nothing to update'));
  }

  if (hasAdd && hasUniquenessConstraint(propertyKey)) {
    const valuesToAdd = sanitizedDelta[add];
    assertPrecondition(valuesToAdd !== undefined);
    const dedupedValues = removeDuplicates(valuesToAdd);
    return Result.ok(MfsDeltaValue.from({ addValues: dedupedValues, removeValues: sanitizedDelta[remove] }));
  }

  return Result.ok(sanitizedDelta);
}

// Currently only supported for base properties (`secondary labels`).
const hasUniquenessConstraint = (property: string): boolean => hasTraits(property, MfsPropertyTrait.Unique);

function checkDelta(propertyValue: MfsDeltaValue): { hasAdd: boolean; hasRemove: boolean } {
  const valuesToAdd = propertyValue[add];
  const valuesToRemove = propertyValue[remove];
  return {
    hasAdd: (valuesToAdd && hasAnyElement(valuesToAdd)) ?? false,
    hasRemove: (valuesToRemove && hasAnyElement(valuesToRemove)) ?? false,
  };
}

/** @returns Sanitized property value to set. */
export function getEffectiveValue(
  propertyKey: string,
  propertyValue: MfsValue[] | MfsDeltaValue
): Result<MfsValue[] | MfsDeltaValue> {
  if (isMfsValueArray(propertyValue) && propertyValue !== undefined && propertyValue !== null) {
    return getEffectiveMultiValue(propertyKey, propertyValue);
  }

  if (isMfsDeltaValue(propertyValue)) {
    return getEffectiveDeltaValue(propertyKey, propertyValue);
  }

  return Result.fail(new Error(`failed to get values ${JSON.stringify(propertyValue)}`));
}

export function hasAddRemove(propertyValue: MfsValue[] | MfsDeltaValue): { hasAdd: boolean; hasRemove: boolean } {
  // empty arrays is still a value to be set, so for non-delta hasAdd is always true.
  return isMfsDeltaValue(propertyValue) ? checkDelta(propertyValue) : { hasAdd: true, hasRemove: false };
}

export function getOnlyAddValues(propertyValue: MfsValue | MfsDeltaValue): MfsValue | MfsDeltaValue {
  if (isMfsDeltaValue(propertyValue)) {
    return MfsDeltaValue.from({ addValues: propertyValue[add] });
  }
  return propertyValue;
}
